import { createApi } from "@reduxjs/toolkit/query/react";
import { privateBaseQuery } from "@/api/base/private/privateBaseQuery";

export interface OrderTotalResponse {
  id: number;
  total: { raw: number; templated: string };
  savings: { raw: number; templated: string };
  promocode: string | null;
  is_needs_shipment: boolean;
}

export interface BasketProduct {
  id: number;
  name: string;
  description?: string;
  image?: string;
  is_cyfral?: boolean;
  type?: string;
}

export interface BasketItemAmount {
  new: { raw: number; templated: string };
  old?: { raw: number; templated: string } | null;
}

export interface BasketItem {
  id: number;
  product_id: number;
  product: BasketProduct;
  quantity: number;
  amount: BasketItemAmount;
  options?: Record<string, unknown>;
}

export interface BasketListResponse {
  entities: BasketItem[];
}

export const basketApi = createApi({
  reducerPath: "basketApi",
  baseQuery: privateBaseQuery(),
  tagTypes: ["OrderTotal", "BasketItems"],
  endpoints: (builder) => ({
    addToBasket: builder.mutation<void, { product_id: number }>({
      query: (body) => ({
        url: "/basket/add.json",
        method: "POST",
        data: body,
      }),
      invalidatesTags: ["OrderTotal"],
    }),

    removeFromBasket: builder.mutation<void, { product_id: number }>({
      query: (body) => ({
        url: "/basket/remove.json",
        method: "POST",
        data: body,
      }),
      invalidatesTags: ["OrderTotal"],
    }),

    clearBasket: builder.mutation<void, { ids?: number[] } | void>({
      query: (body) => ({
        url: "/basket/clear.json",
        method: "POST",
        data: body ?? {},
      }),
      invalidatesTags: ["OrderTotal", "BasketItems"],
    }),

    getOrderTotal: builder.query<OrderTotalResponse, void>({
      query: () => ({
        url: "/order/total.json",
        method: "GET",
      }),
      providesTags: ["OrderTotal"],
    }),

    attachPromocode: builder.mutation<void, { promocode: string }>({
      query: (body) => ({
        url: "/order/attach-promocode.json",
        method: "POST",
        data: body,
      }),
      invalidatesTags: ["OrderTotal"],
    }),

    detachPromocode: builder.mutation<void, void>({
      query: () => ({
        url: "/order/detach-promocode.json",
        method: "POST",
      }),
      invalidatesTags: ["OrderTotal"],
    }),

    makePayment: builder.mutation<
      { html: string },
      { providerId: number; isMobile?: boolean }
    >({
      query: ({ providerId, isMobile = false }) => ({
        url: `/payment/make/${providerId}.json`,
        method: "POST",
        data: { is_mobile: isMobile },
      }),
      transformResponse: (response: any) => {
        // Бэкенд возвращает: { status: "success", data: { view: "..." } }
        // Преобразуем в: { html: "..." }
        if (response?.data?.view) {
          return { html: response.data.view };
        }
        if (response?.view) {
          return { html: response.view };
        }
        return { html: "" };
      },
    }),

    getBasketItems: builder.query<BasketItem[], void>({
      query: () => ({
        url: "/basket",
        method: "GET",
        headers: {
          "X-Requested-Fields": "id,amount,quantity,product_price,product:id,product:type,product:shipment_type,product:image,product:name,product:description,product:quantity",
        },
      }),
      transformResponse: (response: any) => {
        if (Array.isArray(response?.data?.entities)) return response.data.entities;
        if (Array.isArray(response?.entities)) return response.entities;
        if (Array.isArray(response?.data)) return response.data;
        if (Array.isArray(response)) return response;
        return [];
      },
      providesTags: ["BasketItems"],
    }),

    updateBasketItem: builder.mutation<void, { id: number; quantity: number }>({
      query: ({ id, quantity }) => ({
        url: `/basket/${id}`,
        method: "PUT",
        data: { quantity },
      }),
      invalidatesTags: ["BasketItems", "OrderTotal"],
    }),

    deleteBasketItem: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        url: `/basket/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BasketItems", "OrderTotal"],
    }),
  }),
});

export const {
  useAddToBasketMutation,
  useRemoveFromBasketMutation,
  useClearBasketMutation,
  useGetOrderTotalQuery,
  useAttachPromocodeMutation,
  useDetachPromocodeMutation,
  useMakePaymentMutation,
  useGetBasketItemsQuery,
  useUpdateBasketItemMutation,
  useDeleteBasketItemMutation,
} = basketApi;
